<script setup lang="ts">
import BootstrapAlert from '@/components/bootstrap/BootstrapAlert.vue';
import { useAlertStore } from '@/services/stores/alertStore';
import { useLoginService } from '@/services/api/loginService';
import { useAuthStore } from '@/services/stores/authStore';
import type { IMfaRecoveryRequest } from '@/types/interfaces/LoginTypes';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Routes from '@/constants/Routes';
import { loginErrorMessage, resolveReturnUrl, returnUrlQuery, routeAfterLogin } from '@/helpers/loginHelpers';
import SubmitButtonContent from '@/components/devextreme/SubmitButtonContent.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import { DxButtonItem, DxButtonOptions, DxForm, DxGroupItem, DxRequiredRule, DxSimpleItem } from 'devextreme-vue/form';
import Icons from '@/constants/Icons';

definePage({
  name: 'LoginMfaRecovery',
  meta: {
    anonymous: true
  }
});

const auth = useAuthStore();
const login = useLoginService();
const alert = useAlertStore();
const router = useRouter();
const route = useRoute();
const returnUrl = computed(() => resolveReturnUrl(router, route.query));
// The other login pages continue with the same page after login.
const returnQuery = computed(() => returnUrlQuery(returnUrl.value?.fullPath));
const loading = ref<boolean>(false);
const recovery = ref<IMfaRecoveryRequest>({
  recoveryCode: ''
});

async function onSubmit(): Promise<void> {
  loading.value = true;
  const response = await login.verifyRecoveryCode(recovery.value);
  if (response && response.success && response.data) {
    auth.initialize(response.data.token);

    await router.push(routeAfterLogin(auth, false, returnUrl.value));
    return;
  }

  recovery.value.recoveryCode = '';
  loading.value = false;
  alert.showError(loginErrorMessage(response.problem));
}
</script>

<template>
  <BootstrapAlert type="light" class="d-flex justify-content-center mb-3">
    {{ $t('login.recoveryIntro') }}
  </BootstrapAlert>

  <form @submit.prevent="onSubmit">
    <DxForm :disabled="loading" :form-data="recovery" label-mode="floating">
      <DxSimpleItem data-field="recoveryCode">
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.recoveryCode') })" />
      </DxSimpleItem>

      <DxGroupItem :col-count="2">
        <DxButtonItem>
          <DxButtonOptions template="sendButtonTemplate" type="primary" :use-submit-behavior="true" width="100%" />
        </DxButtonItem>
        <DxButtonItem>
          <DxButtonOptions template="cancelButtonTemplate" type="secondary" width="100%" />
        </DxButtonItem>
      </DxGroupItem>

      <template #sendButtonTemplate>
        <SubmitButtonContent :loading="loading" :icon="Icons.SignInAlt" :text="$t('login.useRecoveryCode')" />
      </template>

      <template #cancelButtonTemplate>
        <ButtonContent :text="$t('common.cancel')" :icon="Icons.Ban" :to="{ name: Routes.Login.Root, query: returnQuery }" />
      </template>
    </DxForm>
  </form>
</template>

<style scoped>

</style>